import React, { Component } from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import Select from "components/Common/Select";
import InputRange from "components/Common/InputRange";
import InfoTooltip from "components/Common/InfoTooltip";
import classNames from "classnames";
import { convertToMS } from "services/time";
import "./style.scss";

const PERSISTENCE_OPTIONS = [
  {
    label: "Never Forget",
    value: "normal"
  },
  {
    label: "Forget After Tab Close",
    value: "session"
  },
  {
    label: "Forget After Reload",
    value: "private"
  },
  {
    label: "Forget After...",
    value: "custom"
  }
];

const TIME_OPTIONS = [
  {
    label: "Minute(s)",
    value: "mins"
  },
  {
    label: "Hour(s)",
    value: "hours"
  },
  {
    label: "Day(s)",
    value: "days"
  },
  {
    label: "Week(s)",
    value: "weeks"
  }
];

class SettingsPlatformChatModalFunctionality extends Component {
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);
    const { platform } = props;
    const { timeToExpiry, persistence } = platform.get("record");
    const persistenceDisplayed = persistence === "normal" && timeToExpiry
      ? "custom"
      : persistence;

    this.state = {
      persistenceDisplayed
    };
    this.onPersistenceChange = this.onPersistenceChange.bind(this);
    this.onRolloutChange = this.onRolloutChange.bind(this);
    this.updateTimeToExpiry = this.updateTimeToExpiry.bind(this);
    this.onTimeChange = this.onTimeChange.bind(this);
    this.onTimeUnitChange = this.onTimeUnitChange.bind(this);
  }

  /**
   * @param {Event} value
   */
  onPersistenceChange(value) {
    const { platformType, updatePlatform } = this.props;
    this.setState({
      persistenceDisplayed: value
    });

    if (value === "custom") {
      updatePlatform(platformType, { persistence: "normal" });
    } else {
      updatePlatform(platformType, {
        persistence: value,
        timeToExpiry: 0
      });
    }
  }

  /**
   * @param {Event} event the event when a user slides the rollout
   */
  onRolloutChange(event) {
    const { platformType, updatePlatform } = this.props;
    const rollout = event.target.value / 100;
    updatePlatform(platformType, { rollout });
  }

  /**
   * @param {Event} event the time entered
   */
  onTimeChange(event) {
    const { platform } = this.props;
    const time = event.target.value;

    if (time >= 0 && time <= 9999) {
      this.updateTimeToExpiry(
        platform.getIn(["record", "expiryUnitOfTime"]),
        event.target.value
      );
    }
  }

  /**
   * @param {String} value the unit of time selected
   */
  onTimeUnitChange(value) {
    const { platform } = this.props;
    this.updateTimeToExpiry(
      value,
      platform.getIn(["record", "expiryTime"])
    );
  }

  /**
   * Updates timeToExpiry
   * @param  {String} expiryUnitOfTime   unit of Time
   * @param  {Number} expiryTime    amount of time
   */
  updateTimeToExpiry(expiryUnitOfTime, expiryTime) {
    const { platformType, updatePlatform } = this.props;
    const timeToExpiry = convertToMS(expiryUnitOfTime, expiryTime);

    updatePlatform(
      platformType,
      { timeToExpiry, expiryUnitOfTime, expiryTime }
    );
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const { platform } = this.props;
    const { persistenceDisplayed } = this.state;
    const isCustom = persistenceDisplayed === "custom";

    return (
      <div className="Functionality Modal__content">
        <label
          htmlFor="rollout"
          className="g-input__label"
        >
          Rollout
        </label>
        <p
          className="g-input__description"
        >
          Use the rollout slider to probabilistically control what
          percentage of your customers see the bot on your site.
        </p>
        <InputRange
          id="rollout"
          min={0}
          max={100}
          step={5}
          value={platform.get("record").rollout * 100}
          onChange={this.onRolloutChange}
          className="SettingsPlatformChatModalFunctionality__range-input"
        />
        <div>
          <label
            htmlFor="persistence"
            className="g-input__label g-input__label--no-description"
          >
            Chatter Persistence
            <InfoTooltip
              customClassName="SettingsPlatformChatModalFunctionality__tooltip"
              blurb="Select when to forget the chatter."
              iconDefault="QuestionCircle"
              iconClicked="QuestionCircleFilled"
              inModal
              customPosition={{
                dimple: "down",
                top: -60,
                left: -80
              }}
            />
          </label>
        </div>
        <div className="SettingsPlatformChatModalFunctionality__selectors">
          <Select
            id="persistence"
            onChange={this.onPersistenceChange}
            value={persistenceDisplayed}
            options={PERSISTENCE_OPTIONS}
            placeholder="Choose a Persistence Mode"
            customClassName="SettingsPlatformsChatModal__select"
          />
          {isCustom && (
            <>
              <input
                className={classNames(
                  "g-input SettingsPlatformChatModalFunctionality__selectors__time-input",
                  { "g-input--invalid": !platform.getIn(["record", "expiryTime"]) }
                )}
                type="number"
                placeholder="1"
                value={platform.getIn(["record", "expiryTime"])}
                onChange={this.onTimeChange}
              />
              <Select
                id="persistence"
                onChange={this.onTimeUnitChange}
                value={platform.getIn(["record", "expiryUnitOfTime"])}
                options={TIME_OPTIONS}
                placeholder="Unit"
                customClassName="SettingsPlatformsChatModal__select"
              />
            </>
          )}
        </div>
      </div>
    );
  }
}

SettingsPlatformChatModalFunctionality.propTypes = {
  platform: ImmutablePropTypes.map.isRequired,
  platformType: PropTypes.string.isRequired,
  updatePlatform: PropTypes.func.isRequired
};

export default SettingsPlatformChatModalFunctionality;
